import classNames from 'classnames'
import Tooltip from '@dustin-web/microsite-components/src/tooltip'
import { ProductLifeCycleState } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import AvailabiltiySvgIcon from '@dustin-web/microsite-components/src/icon/generated/availability'
import ErrorBoundary from '@dustin-web/microsite-components/src/utils/error-boundary'
import type { GenericAvailabilityType } from '../cards'

const AvailabilityIcon = ({ color, className }: { color: string; className?: string }) => (
  <AvailabiltiySvgIcon
    className={`c-availability__icon c-availability__icon--${color} ${className}`}
    multiLayered
  />
)

const bannedLifeCycleStatesForPage: ProductLifeCycleState[] = [
  ProductLifeCycleState.EndOfLife,
  ProductLifeCycleState.TempStopped,
  ProductLifeCycleState.NotReleased,
]

type Props = {
  availability?: GenericAvailabilityType | null
  isBid?: boolean
  listForm?: boolean
  className?: string
  iconClassName?: string
  showOnlyIcon?: boolean
  availabilityTextClassName?: boolean
  defaultContainerClassName?: string
  infoTextClassName?: string
  isEstimatedShippingDate?: boolean
  color: string
  text: AvailabilityTexts
}

type AvailabilityTexts = {
  stockText: string
  tooltipText: string
  bidInfo: string
}

export const ProductAvailability = (props: Props) => {
  return (
    <ErrorBoundary>
      <ProductAvailabilityInternal {...props} />
    </ErrorBoundary>
  )
}

const ProductAvailabilityInternal = ({
  availability,
  isBid = false,
  listForm = false,
  className = '',
  iconClassName = '',
  availabilityTextClassName = false,
  showOnlyIcon = false,
  infoTextClassName,
  defaultContainerClassName,
  isEstimatedShippingDate,
  color,
  text,
}: Props) => {
  if (
    !availability ||
    !availability.productLifeCycleState ||
    bannedLifeCycleStatesForPage.includes(availability.productLifeCycleState)
  ) {
    return null
  }

  if (listForm || isEstimatedShippingDate) {
    return (
      <ListOrEstimatedShippingDate
        tooltipText={text.tooltipText}
        color={color}
        iconClassName={iconClassName}
        stockText={text.stockText}
        className={className}
        showOnlyIcon={showOnlyIcon}
        defaultContainerClassName={defaultContainerClassName}
      />
    )
  }

  return (
    <Default
      className={className}
      color={color}
      iconClassName={iconClassName}
      useAvailabilityStockTextClassName={availabilityTextClassName}
      stockText={text.stockText}
      isBid={isBid}
      infoText={text.bidInfo}
      infoTextClassName={infoTextClassName}
      defaultContainerClassName={defaultContainerClassName}
    />
  )
}

type ListOrEstimatedShippingDateProps = {
  tooltipText: string
  color: string
  iconClassName?: string
  stockText: string
  className?: string
  showOnlyIcon?: boolean
  defaultContainerClassName?: string
  stockTextClassName?: string
}
const ListOrEstimatedShippingDate = ({
  tooltipText,
  color,
  iconClassName,
  stockText,
  className,
  showOnlyIcon,
  defaultContainerClassName = 'c-availability',
  stockTextClassName = 'c-availability__text',
}: ListOrEstimatedShippingDateProps) => (
  <Tooltip tooltipText={tooltipText} className={classNames(defaultContainerClassName, className)}>
    <AvailabilityIcon color={color} className={iconClassName} />
    {!showOnlyIcon && <span className={stockTextClassName}>{stockText}</span>}
  </Tooltip>
)
type DefaultProps = {
  className?: string
  defaultContainerClassName?: string
  stockClassName?: string
  infoTextClassName?: string
  color: string
  iconClassName?: string
  useAvailabilityStockTextClassName?: boolean
  stockText: string
  isBid: boolean
  infoText: string
}
const Default = ({
  className,
  defaultContainerClassName = 'c-availability',
  stockClassName = 'c-availability__text',
  infoTextClassName = 'u-py-8 u-pl-24',
  color,
  iconClassName,
  useAvailabilityStockTextClassName,
  stockText,
  isBid,
  infoText,
}: DefaultProps) => (
  <div>
    <div className={classNames(defaultContainerClassName, className)}>
      <AvailabilityIcon color={color} className={iconClassName} />
      <span
        className={classNames(stockClassName, {
          'c-availability__product-information-text': useAvailabilityStockTextClassName,
        })}>
        {stockText}
      </span>
    </div>
    {isBid && <div className={infoTextClassName}>{infoText}</div>}
  </div>
)
export default ProductAvailability
